import type { BuyLogSummary } from "@/hooks/use-budgets";
import { formatCurrency, formatPercent } from "@/lib/formatters";

interface BuyLogSummaryBarProps {
  summary: BuyLogSummary;
}

interface Metric {
  label: string;
  value: string;
  hint?: string;
  emphasis?: "positive" | "negative" | "neutral";
}

/**
 * Five-metric header shown above the Buy Log table. Mirrors the spec §10
 * summary metrics.
 */
export function BuyLogSummaryBar({ summary }: BuyLogSummaryBarProps) {
  const varianceEmphasis: Metric["emphasis"] =
    summary.totalVariance > 0 ? "positive" : summary.totalVariance < 0 ? "negative" : "neutral";

  const metrics: Metric[] = [
    { label: "Total Budget", value: formatCurrency(summary.totalBudget) },
    { label: "Total Awarded", value: formatCurrency(summary.totalAwarded) },
    { label: "Total Committed", value: formatCurrency(summary.totalCommitted) },
    {
      label: "Total Variance",
      value: formatCurrency(summary.totalVariance),
      emphasis: varianceEmphasis,
    },
    {
      label: "% Bought Out",
      value: formatPercent(summary.percentBoughtOut),
      hint: `${summary.awardedCount} of ${summary.lineCount} lines awarded`,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
      {metrics.map((m) => (
        <div key={m.label} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
            {m.label}
          </div>
          <div
            className={
              m.emphasis === "positive"
                ? "mt-1 text-xl font-semibold text-emerald-600"
                : m.emphasis === "negative"
                  ? "mt-1 text-xl font-semibold text-red-600"
                  : "mt-1 text-xl font-semibold text-gray-900"
            }
          >
            {m.value}
          </div>
          {m.hint ? <div className="mt-1 text-xs text-gray-500">{m.hint}</div> : null}
        </div>
      ))}
    </div>
  );
}
